import {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch } from "../store/hooks";
import {
  getAveragesThunk,
  getKeepaThunk,
  getLookupThunk,
  getNotesThunk,
  getProductDetails,
  getProductHistoriesThunk,
  getProductTagsThunk,
  // getRanks,
} from "../features/productAnalyzer/productAnalyzer.thunk";
import { getAmazonCredentialsThunk } from "../features/user/user.thunk";
import { getSellersThunk } from "../features/sellers/sellers.thunk";
import { store } from "../store/store";
import { DURATION_OPTIONS } from "../pages/ProductAnalyzer/common/DurationFilter";
import {
  AMZ_RESPONSES,
  NO_SEARCH_RESULTS,
  Panel,
  PLAN_LIMIT_EXCEEDED_ERR,
} from "../constants";
import useProductAnalyzerParams from "../hooks/useProductAnalyzerParams";
import { AxiosError } from "axios";
import {
  TEligibility,
  TErrorResponse,
} from "../features/productAnalyzer/productAnalyzer.types";
import { isAmzLoginPage } from "../helpers";
import { updateEligibility } from "../features/productAnalyzer/productAnalyzer.slice";
import useExtension from "../hooks/useExtension";
import { setError } from "../features/search/search.slice";
import { extractSellerIds } from "../utils/productAnalzyer/buyboxAnalysis";

type TPanelStatus = "loading" | "succeeded" | "failed";

const DEFAULT_PANEL_STATUS: Record<Panel, TPanelStatus> = {
  [Panel.PRODUCT]: "loading",
  [Panel.QUICK_INFO]: "loading",
  [Panel.PROFIT_CALCULATOR]: "loading",
  [Panel.CHARTS]: "loading",
  [Panel.RANKS_AND_PRICES]: "loading",
  [Panel.ALERTS]: "loading",
  [Panel.DISCOUNTS]: "loading",
  [Panel.NOTES_AND_TAGS]: "loading",
  [Panel.MARKETPLACES]: "loading",
  [Panel.SELLER_CENTRAL]: "loading",
  [Panel.SEARCH_AGAIN]: "succeeded",
  [Panel.OFFERS]: "loading",
  [Panel.BUY_BOX_ANALYSIS]: "loading",
  [Panel.ADDITIONAL_EXPENSES]: "loading",
  [Panel.VARIATIONS]: "loading",
  [Panel.KEEPA]: "loading",
  [Panel.ROI]: "loading",
  [Panel.ASIN_STATISTIC]: "loading",
  [Panel.HISTORY]: "loading",
};

interface ProductAnalyzerContextProps {
  asin: string;
  panelStatus: Record<Panel, TPanelStatus>;
  isLoading: boolean;
  openQuickList: boolean;
  setOpenQuickList: (val: boolean) => void;
}

export const ProductAnalyzerContext =
  createContext<ProductAnalyzerContextProps | null>(null);

export const useProductAnalyzer = () => {
  const ctx = useContext(ProductAnalyzerContext);
  if (!ctx) {
    throw new Error(
      "useProductAnalyzer must be used within a ProductAnalyzerProvider",
    );
  }
  return ctx;
};

export const ProductAnalyzerProvider = ({
  children,
}: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const [panelStatus, setPanelStatus] =
    useState<Record<Panel, TPanelStatus>>(DEFAULT_PANEL_STATUS);
  const [openQuickList, setOpenQuickList] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { fetchExtension } = useExtension();

  const { asin, setAsin, lookupId } = useProductAnalyzerParams();

  const fetchSellersFromState = () => {
    const state = store.getState();
    const offers = state.productAnalyzer.offers;
    const buyBoxSellerIdHistory = state.productAnalyzer.buyBoxSellerIdHistory;
    const lastUpdate = state.productAnalyzer.lastUpdate;
    let sellerIds = extractSellerIds(buyBoxSellerIdHistory, lastUpdate);
    // const sellerIds = new Set<string>();

    if (offers && Array.isArray(offers)) {
      offers.forEach((offer) => {
        if (offer?.sellerId) {
          sellerIds.push(offer.sellerId);
        }
      });
    }

    // Filter unique IDs and remove SUPPRESSED (-1)
    sellerIds = Array.from(new Set(sellerIds)).filter(
      (id) => id !== "-1" && id !== "-2",
    );

    if (sellerIds.length > 0) {
      const existingSellers = state.sellers.entities;
      const idsToFetch = sellerIds.filter((id) => !existingSellers[id]);

      if (idsToFetch.length > 0) {
        dispatch(getSellersThunk(idsToFetch)).catch((error) => {
          console.error("Failed to fetch sellers:", error);
        });
      }
    }
  };

  const lastAsinRef = useRef<string | null>(null);

  const asinFromUrl = searchParams.get("asin");

  const isLoading = useMemo(
    () =>
      !!Object.values(panelStatus).find(
        (status: TPanelStatus) => status === "loading",
      ),
    [panelStatus],
  );

  useEffect(() => {
    if (asinFromUrl) {
      setAsin(asinFromUrl);
    }
  }, [asinFromUrl, setAsin]);

  useEffect(() => {
    if (!asin) return;

    if (lastAsinRef.current === asin) return;

    lastAsinRef.current = asin;
    setPanelStatus(DEFAULT_PANEL_STATUS);
    dispatch(getAmazonCredentialsThunk());

    dispatch(getProductDetails({ asin, lookupId }))
      .unwrap()
      .then((res) => {
        if (res.lookupId) {
          setSearchParams({ asin, lookupId: res.lookupId });
        }

        if (lookupId) {
          dispatch(getLookupThunk(lookupId))
            .unwrap()
            .then(() => {
              setPanelStatus((prev) => ({
                ...prev,
                [Panel.PRODUCT]: "succeeded",
                [Panel.PROFIT_CALCULATOR]: "succeeded",
                [Panel.ALERTS]: "succeeded",
                [Panel.DISCOUNTS]: "succeeded",
                [Panel.MARKETPLACES]: "succeeded",
                [Panel.SELLER_CENTRAL]: "succeeded",
                [Panel.ADDITIONAL_EXPENSES]: "succeeded",
                [Panel.VARIATIONS]: "succeeded",
                [Panel.ROI]: "succeeded",
                [Panel.QUICK_INFO]: "succeeded",
                [Panel.RANKS_AND_PRICES]: "succeeded",
                [Panel.BUY_BOX_ANALYSIS]: "succeeded",
                [Panel.ASIN_STATISTIC]: "succeeded",
                [Panel.OFFERS]: "succeeded",
                [Panel.CHARTS]: "succeeded",
              }));
              fetchSellersFromState();
            })
            .catch(() => {
              setPanelStatus((prev) => ({
                ...prev,
                [Panel.PRODUCT]: "failed",
                [Panel.PROFIT_CALCULATOR]: "failed",
                [Panel.ALERTS]: "failed",
                [Panel.DISCOUNTS]: "failed",
                [Panel.MARKETPLACES]: "failed",
                [Panel.SELLER_CENTRAL]: "failed",
                [Panel.ADDITIONAL_EXPENSES]: "failed",
                [Panel.VARIATIONS]: "failed",
                [Panel.ROI]: "failed",
                [Panel.QUICK_INFO]: "failed",
                [Panel.RANKS_AND_PRICES]: "failed",
                [Panel.BUY_BOX_ANALYSIS]: "failed",
                [Panel.ASIN_STATISTIC]: "failed",
                [Panel.OFFERS]: "failed",
                [Panel.CHARTS]: "failed",
              }));
            });
        } else {
          setPanelStatus((prev) => ({
            ...prev,
            [Panel.PRODUCT]: "succeeded",
            [Panel.PROFIT_CALCULATOR]: "succeeded",
            [Panel.ALERTS]: "succeeded",
            [Panel.DISCOUNTS]: "succeeded",
            [Panel.MARKETPLACES]: "succeeded",
            [Panel.SELLER_CENTRAL]: "succeeded",
            [Panel.ADDITIONAL_EXPENSES]: "succeeded",
            [Panel.VARIATIONS]: "succeeded",
            [Panel.ROI]: "succeeded",
            [Panel.QUICK_INFO]: "succeeded",
            [Panel.RANKS_AND_PRICES]: "succeeded",
            [Panel.BUY_BOX_ANALYSIS]: "succeeded",
            [Panel.ASIN_STATISTIC]: "succeeded",
            [Panel.OFFERS]: "succeeded",
            [Panel.CHARTS]: "succeeded",
          }));
          fetchSellersFromState();
        }
        Promise.all([
          dispatch(getNotesThunk(asin)).unwrap(),
          dispatch(getProductTagsThunk(asin)).unwrap(),
        ])
          .then(() => {
            setPanelStatus((prev) => ({
              ...prev,
              [Panel.NOTES_AND_TAGS]: "succeeded",
            }));
          })
          .catch(() => {
            setPanelStatus((prev) => ({
              ...prev,
              [Panel.NOTES_AND_TAGS]: "failed",
            }));
          });
        dispatch(getKeepaThunk({ asin, country: "US", day: 30 }))
          .unwrap()
          .then(() => {
            setPanelStatus((prev) => ({
              ...prev,
              [Panel.KEEPA]: "succeeded",
            }));
          })
          .catch(() => {
            setPanelStatus((prev) => ({
              ...prev,
              [Panel.KEEPA]: "failed",
            }));
          });
        dispatch(getProductHistoriesThunk({ asin, page: 1, perPage: 5 }))
          .unwrap()
          .then(() => {
            setPanelStatus((prev) => ({
              ...prev,
              [Panel.HISTORY]: "succeeded",
            }));
          })
          .catch(() => {
            setPanelStatus((prev) => ({
              ...prev,
              [Panel.HISTORY]: "failed",
            }));
          });

        fetchExtension({
          source: `https://sellercentral.amazon.com/productsearch/v2/search?q=${asin}&page=1`,
          config: {
            credentials: "include",
            headers: {
              "x-requested-with": "XMLHttpRequest",
            },
          },
        })
          .then((fetchResult) => {
            console.debug("Fetched extension: ", fetchResult);
            const lResult = {
              result: AMZ_RESPONSES.UNKNOWN,
              newQualificationMessages: [],
            };
            if (fetchResult === null) {
              console.debug("No Bridge: " + chrome.runtime?.lastError?.message);
              lResult.result = AMZ_RESPONSES.MISCONFIG;
              return lResult;
            } else if (fetchResult.noInstall) {
              lResult.result = AMZ_RESPONSES.NO_EXTENSION;
              return lResult;
            } else if (fetchResult.error) {
              console.error(fetchResult.error);
              return lResult;
            } else if (fetchResult.status && fetchResult.status >= 400) {
              lResult.result = AMZ_RESPONSES.NOT_LOGGED_IN;
              return lResult;
            } else {
              if (fetchResult.html && fetchResult.html !== "") {
                const doc = new DOMParser().parseFromString(
                  fetchResult.html,
                  "text/html",
                );
                const lJsonContents = doc.querySelector("pre");
                if (lJsonContents) {
                  try {
                    fetchResult.json = JSON.parse(
                      lJsonContents.innerText.trim(),
                    );
                  } catch (_) {
                    fetchResult.json = null;
                  }
                }
              }
              if (isAmzLoginPage(fetchResult.html)) {
                lResult.result = AMZ_RESPONSES.NOT_LOGGED_IN;
                return lResult;
              } else if (
                fetchResult.html !== null &&
                (fetchResult.html?.indexOf("Not Authorised") !== -1 ||
                  fetchResult.html?.indexOf("Invalid Access") !== -1)
              ) {
                lResult.result = AMZ_RESPONSES.NOT_AUTHORISED;
                return lResult;
              } else if (
                fetchResult.html !== null &&
                fetchResult.html.indexOf(
                  "Select a Merchant and Marketplace",
                ) !== -1
              ) {
                lResult.result = AMZ_RESPONSES.SELECT_MARKETPLACE;
                return lResult;
              } else if (!fetchResult.json) {
                return lResult;
              } else if (!Array.isArray(fetchResult.json.products)) {
                if (
                  fetchResult.html &&
                  fetchResult.html.indexOf("cross-regional accounts") !== -1
                ) {
                  lResult.result = AMZ_RESPONSES.RESPONSE_ENABLE_SINGLE_SIGN_IN;
                  return lResult;
                }
                lResult.result = AMZ_RESPONSES.RESPONSE_NOT_RESTRICTED;
                return lResult;
              } else if (fetchResult.json.products.length) {
                const product = fetchResult.json.products[0];
                try {
                  let lRestricted = false;
                  lRestricted =
                    lRestricted ||
                    (product.restrictedForAnyCondition &&
                      product.restrictedForAllConditions);
                  // lResult.pathToSellUrl = product.pathToSellUrl;
                  lRestricted =
                    lRestricted ||
                    (product.pathToSellUrl &&
                      product.pathToSellUrl.indexOf("approvalrequest") > -1);
                  const lNewQualificationMessages =
                    product.qualificationMessages.filter(
                      (item: any) =>
                        item.conditionList == null ||
                        item.conditionList.indexOf("New") !== -1,
                    );
                  lResult.newQualificationMessages =
                    lNewQualificationMessages.map(
                      (a: any) => a.qualificationMessage,
                    );
                  lRestricted =
                    lRestricted || lNewQualificationMessages.length > 0;
                  lResult.result = lRestricted
                    ? AMZ_RESPONSES.RESPONSE_RESTRICTED
                    : AMZ_RESPONSES.RESPONSE_NOT_RESTRICTED;
                } catch (error) {
                  console.error(error);
                }
              } else {
                lResult.result = AMZ_RESPONSES.RESPONSE_NOT_RESTRICTED;
              }
            }
            return lResult;
          })
          .then((lResult) => {
            if (lResult?.result === AMZ_RESPONSES.MISCONFIG) {
              dispatch(
                updateEligibility({
                  eligible: "-",
                }),
              );
            } else if (lResult?.result === AMZ_RESPONSES.NO_EXTENSION) {
              dispatch(
                updateEligibility({
                  eligible: "Not Connected",
                  hint: "&#x26A0;&#xFE0F; Unable to detect eligibility, ensure extension is installed and you are logged in on Seller Central",
                }),
              );
            } else if (
              lResult?.result === AMZ_RESPONSES.RESPONSE_NOT_RESTRICTED
            ) {
              dispatch(
                updateEligibility({
                  eligible: "Yes",
                  hint: `&#x1F44D; You are eligible to sell this product.`,
                }),
              );
            } else if (lResult?.result === AMZ_RESPONSES.RESPONSE_RESTRICTED) {
              dispatch(
                updateEligibility({
                  eligible: "No",
                  hint: `&#x1F44E; ${lResult.newQualificationMessages.join("<br>")}`,
                }),
              );
            } else {
              dispatch(
                updateEligibility({
                  eligible: "Unknown",
                  hint: "&#x26A0;&#xFE0F; Unable to detect eligibility, ensure you are logged in and have the correct permissions on Seller Central",
                }),
              );
            }
          });
      })
      .catch((e) => {
        const error = e as AxiosError<TErrorResponse>;
        if (error.status === 403) {
          if (
            error.response?.data?.ErrorMessage?.includes(
              PLAN_LIMIT_EXCEEDED_ERR,
            )
          ) {
            navigate("/account/subscriptions");
          }
        } else {
          setPanelStatus(() => ({
            [Panel.PRODUCT]: "failed",
            [Panel.QUICK_INFO]: "failed",
            [Panel.PROFIT_CALCULATOR]: "failed",
            [Panel.CHARTS]: "failed",
            [Panel.RANKS_AND_PRICES]: "failed",
            [Panel.ALERTS]: "failed",
            [Panel.DISCOUNTS]: "failed",
            [Panel.NOTES_AND_TAGS]: "failed",
            [Panel.MARKETPLACES]: "failed",
            [Panel.SELLER_CENTRAL]: "failed",
            [Panel.SEARCH_AGAIN]: "succeeded",
            [Panel.OFFERS]: "failed",
            [Panel.BUY_BOX_ANALYSIS]: "failed",
            [Panel.ADDITIONAL_EXPENSES]: "failed",
            [Panel.VARIATIONS]: "failed",
            [Panel.KEEPA]: "failed",
            [Panel.ROI]: "failed",
            [Panel.ASIN_STATISTIC]: "failed",
            [Panel.HISTORY]: "failed",
          }));
          if (error.status === 400) {
            dispatch(
              setError(error.response?.data?.ErrorMessage || NO_SEARCH_RESULTS),
            );
          } else {
            dispatch(setError(NO_SEARCH_RESULTS));
          }
          navigate(`/product-analyzer?q=${asin}&noProducts=true`);
        }

        lastAsinRef.current = null;
      });
    dispatch(getAveragesThunk(asin))
      .unwrap()
      .then(() => {
        // setPanelStatus((prev) => ({
        //   ...prev,
        //   [Panel.ASIN_STATISTIC]: "succeeded",
        //   [Panel.OFFERS]: "succeeded",
        //   [Panel.CHARTS]: "succeeded",
        // }));
        // Fetch sellers after averages data is loaded (may have new offers)
        fetchSellersFromState();
      })
      .catch(() => {
        // setPanelStatus((prev) => ({
        //   ...prev,
        //   [Panel.ASIN_STATISTIC]: "failed",
        //   [Panel.OFFERS]: "failed",
        //   [Panel.CHARTS]: "failed",
        // }));
      });
  }, [asin, dispatch, lookupId, setSearchParams]);

  return (
    <ProductAnalyzerContext.Provider
      value={{ asin, panelStatus, isLoading, openQuickList, setOpenQuickList }}
    >
      {children}
    </ProductAnalyzerContext.Provider>
  );
};
